"use client";

import React, { useState, useEffect } from "react";
import { ArrowUpRight, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, ShieldCheck, Phone } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
const relatedLinks = [{
  title: "Home Additions",
  to: "/home-additions",
  description: "Expand your living space with master suites, sunrooms, second-story expansions, and customized structural additions."
}, {
  title: "Interior Remodeling",
  to: "/interior-remodeling",
  description: "Transform main-floor layouts, architectural trim, built-in cabinetry, and multi-room interior renovations."
}, {
  title: "Remodeling Planning Guide",
  to: "/remodeling-planning-guide",
  description: "Explore realistic timelines, budgeting considerations, material selections, and our step-by-step design process."
}];
const projectTypeOptions = ["Kitchen Remodeling", "Bathroom Remodeling", "Whole-Home Remodeling", "Home Additions", "Basement Remodeling", "Interior Remodeling", "Outdoor Living", "Deck Remodeling", "Other"];
const contactMethodOptions = ["Phone", "Text", "Email"];
const timingOptions = ["ASAP", "Within 1 month", "1-3 months", "3-6 months", "Just researching"];
const budgetOptions = ["Under $25,000", "$25,000-$50,000", "$50,000-$100,000", "$100,000+", "Not sure yet"];
export default function RelatedLinksAndEstimateForm() {
  const [currentPage, setCurrentPage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    project_type: "",
    details: "",
    contact_method: "",
    timing: "",
    budget: "",
    consent: false
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentPage(window.location.pathname || "/");
    }
  }, []);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage("");
    if (!formData.consent) {
      setErrorMessage("Please accept the contact agreement to submit your request.");
      return;
    }
    if (!formData.name || !formData.phone || !formData.email || !formData.address || !formData.project_type || !formData.contact_method || !formData.timing) {
      setErrorMessage("Please complete all required fields.");
      return;
    }
    setIsSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append("name", formData.name);
      params.append("phone", formData.phone);
      params.append("email", formData.email);
      params.append("address", formData.address);
      params.append("project_type", formData.project_type);
      params.append("details", formData.details || "");
      params.append("contact_method", formData.contact_method);
      params.append("timing", formData.timing);
      params.append("budget", formData.budget || "");
      params.append("consent", formData.consent ? "true" : "false");
      params.append("website", "DecaturHomeRenovation.com");
      params.append("page", currentPage);
      params.append("lead_source", "organic");
      const response = await fetch("https://hooks.zapier.com/hooks/catch/28361868/4mcz4d7/", {
        method: "POST",
        body: params
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
      }
    } catch {
      setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="relatedlinksandestimateform" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Related Links Block */}
        <div className="mb-16 md:mb-20">
          <div className="max-w-2xl mb-8">
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
              Explore related renovation guides
            </h2>
            <p className="mt-3 text-base text-muted-foreground">
              Learn more about our core remodeling specializations and how we plan each project phase.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedLinks.map((link, i) => <div key={link.title} data-index={i} className="h-full">
                <Card className="h-full bg-card/90 border border-border hover:border-foreground/30 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between group">
                  <CardHeader className="p-6 pb-4">
                    <div className="flex items-start justify-between gap-4">
                      <CardTitle className="font-serif text-xl sm:text-2xl font-medium group-hover:text-foreground text-foreground">
                        {link.title}
                      </CardTitle>
                      <span className="p-2 rounded-full bg-secondary text-secondary-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                        <ArrowUpRight className="size-4" />
                      </span>
                    </div>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                      {link.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-6 pt-0">
                    <Button asChild variant="outline" size="sm" className="w-full justify-between font-normal">
                      <Link to={link.to}>
                        <span>Read full details</span>
                        <ArrowUpRight className="size-3.5 opacity-70" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        <Separator className="my-12 md:my-16" />

        {/* Universal Estimate Form Block */}
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">
              Request your project estimate
            </h2>
            <p className="mt-3 text-base text-muted-foreground max-w-xl mx-auto">
              Share the specifics of your renovation. We review every detail and follow up with a clear consultation plan.
            </p>
          </div>

          <Card className="border border-border bg-card/95 backdrop-blur-xs shadow-sm rounded-xl p-6 sm:p-8">
            {isSuccess ? <div className="py-12 px-4 text-center flex flex-col items-center justify-center">
                <div className="size-16 rounded-full bg-secondary flex items-center justify-center text-foreground mb-4">
                  <CheckCircle2 className="size-8" />
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground">
                  Estimate request received
                </h3>
                <p className="mt-3 text-muted-foreground max-w-md text-base leading-relaxed">
                  Thank you for reaching out to Decatur Heritage Renovations. Our team is reviewing your project details and will be in touch shortly through your preferred contact method.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row items-center gap-3">
                  <Button asChild variant="outline">
                    <a href="tel:4708479076">
                      <Phone className="size-4" />
                      <span>Call (470) 847-9076</span>
                    </a>
                  </Button>
                  <Button variant="ghost" onClick={() => {
                setIsSuccess(false);
                setFormData({
                  name: "",
                  phone: "",
                  email: "",
                  address: "",
                  project_type: "",
                  details: "",
                  contact_method: "",
                  timing: "",
                  budget: "",
                  consent: false
                });
              }}>
                    Submit another request
                  </Button>
                </div>
              </div> : <form onSubmit={handleSubmit} className="space-y-6">
                {errorMessage && <Alert variant="destructive">
                    <AlertCircle className="size-4" />
                    <AlertTitle>Notice</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="estimate-name">
                      Full Name <span className="text-destructive">*</span>
                    </Label>
                    <Input id="estimate-name" name="name" type="text" required placeholder="Jane Doe" value={formData.name} onChange={handleInputChange} className="bg-background/80" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimate-phone">
                      Phone Number <span className="text-destructive">*</span>
                    </Label>
                    <Input id="estimate-phone" name="phone" type="tel" required placeholder="Your phone number" value={formData.phone} onChange={handleInputChange} className="bg-background/80" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="estimate-email">
                      Email Address <span className="text-destructive">*</span>
                    </Label>
                    <Input id="estimate-email" name="email" type="email" required placeholder="Your email address" value={formData.email} onChange={handleInputChange} className="bg-background/80" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimate-address">
                      Street Address or ZIP Code <span className="text-destructive">*</span>
                    </Label>
                    <Input id="estimate-address" name="address" type="text" required placeholder="e.g. 30030 or Decatur address" value={formData.address} onChange={handleInputChange} className="bg-background/80" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="estimate-project-type">
                      Project Type <span className="text-destructive">*</span>
                    </Label>
                    <Select value={formData.project_type} onValueChange={val => handleSelectChange("project_type", val)} required>
                      <SelectTrigger id="estimate-project-type" className="w-full bg-background/80">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectTypeOptions.map(opt => <SelectItem key={opt} value={opt}>
                            {opt}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimate-contact-method">
                      Preferred Contact Method <span className="text-destructive">*</span>
                    </Label>
                    <Select value={formData.contact_method} onValueChange={val => handleSelectChange("contact_method", val)} required>
                      <SelectTrigger id="estimate-contact-method" className="w-full bg-background/80">
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent>
                        {contactMethodOptions.map(opt => <SelectItem key={opt} value={opt}>
                            {opt}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="estimate-timing">
                      Project Timing <span className="text-destructive">*</span>
                    </Label>
                    <Select value={formData.timing} onValueChange={val => handleSelectChange("timing", val)} required>
                      <SelectTrigger id="estimate-timing" className="w-full bg-background/80">
                        <SelectValue placeholder="Select timeframe" />
                      </SelectTrigger>
                      <SelectContent>
                        {timingOptions.map(opt => <SelectItem key={opt} value={opt}>
                            {opt}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estimate-budget">
                    Estimated Budget <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Select value={formData.budget} onValueChange={val => handleSelectChange("budget", val)}>
                    <SelectTrigger id="estimate-budget" className="w-full bg-background/80">
                      <SelectValue placeholder="Select budget range (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {budgetOptions.map(opt => <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="estimate-details">
                    Project Details <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Textarea id="estimate-details" name="details" rows={4} placeholder="Describe your goals, home style, specific rooms, or existing layout..." value={formData.details} onChange={handleInputChange} className="bg-background/80" />
                </div>

                <div className="pt-2">
                  <div className="flex items-start gap-3 rounded-lg border border-border p-4 bg-muted/40">
                    <Checkbox id="estimate-consent" checked={formData.consent} onCheckedChange={checked => setFormData(prev => ({
                  ...prev,
                  consent: checked === true
                }))} className="mt-0.5" />
                    <div className="grid gap-1">
                      <Label htmlFor="estimate-consent" className="text-xs sm:text-sm text-foreground font-normal cursor-pointer leading-snug">
                        I agree to be contacted about my project by phone, text, or email.
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Hidden tracking parameters */}
                <input type="hidden" name="website" value="DecaturHomeRenovation.com" />
                <input type="hidden" name="page" value={currentPage} />
                <input type="hidden" name="lead_source" value="organic" />

                <div className="pt-2">
                  <Button type="submit" size="lg" disabled={isSubmitting} className="w-full text-base font-medium transition-all">
                    {isSubmitting ? <>
                        <Loader2 className="size-4 animate-spin" />
                        <span>Submitting estimate request...</span>
                      </> : "Submit Estimate Request"}
                  </Button>
                </div>

                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground text-center pt-2">
                  <ShieldCheck className="size-4 text-muted-foreground shrink-0" />
                  <span>Your information is held in strict confidence and used solely for your estimate.</span>
                </div>
              </form>}
          </Card>
        </div>
      </div>
    </section>;
}